import { supabase } from "./supabase.js";
import { wipeLocalCollection, clearMigrationFlag } from "./migration.js";

// Auth + profile access layer. A thin, app-shaped wrapper over supabase.auth and
// the `profiles` table so the views don't reach into the raw client. Cloud sync
// of the user's collection (the `items` table) is a later phase — this module is
// only accounts + profile.

// ---------- session ----------

export async function getSession() {
	const { data } = await supabase.auth.getSession();
	return data.session;
}

export async function getCurrentUser() {
	const session = await getSession();
	return session?.user ?? null;
}

// Subscribe to auth state changes. Returns the subscription so callers can
// unsubscribe (`sub.unsubscribe()`). Used for the password-recovery deep link.
export function onAuthChange(callback) {
	const { data } = supabase.auth.onAuthStateChange((event, session) => {
		callback(event, session);
	});
	return data.subscription;
}

// ---------- sign up / in / out ----------

// Email confirmation is off for v1, so a successful sign-up returns a live
// session immediately and the `handle_new_user` DB trigger has already created
// the profile (handle = email local-part). We then set the display name the user
// actually typed, which the trigger couldn't know.
export async function signUp({ email, password, displayName }) {
	const { data, error } = await supabase.auth.signUp({ email, password });
	if (error) throw error;

	const name = (displayName ?? "").trim();
	if (name && data.user) {
		// Best-effort: if this fails the account still exists with the default
		// display name; surface the error so the caller can decide.
		const { error: profileError } = await supabase
			.from("profiles")
			.update({ display_name: name })
			.eq("id", data.user.id);
		if (profileError) throw profileError;
	}
	return data;
}

export async function signIn({ email, password }) {
	const { data, error } = await supabase.auth.signInWithPassword({
		email,
		password,
	});
	if (error) throw error;
	return data;
}

// Clear the session AND wipe the local collection so the next user on this device
// starts clean (per the login spec). Safe as of Phase 5: the cloud holds the
// migrated copy. Groups/rankings are device-local and unsynced, so they're
// preserved by wipeLocalCollection. The migration flag is cleared so a future
// sign-in on this device re-evaluates migration for whatever data it then holds.
export async function signOut() {
	const { error } = await supabase.auth.signOut();
	if (error) throw error;
	await wipeLocalCollection();
	clearMigrationFlag();
}

// ---------- account deletion ----------

// Delete the signed-in account and everything in it (Supabase RPC
// delete_my_account — design_documents/migrations/2026-10-14-delete-account.sql),
// then clear this device and sign out. Throws err.code = "NOT_SET_UP" when the
// function hasn't been installed in Supabase yet.
export async function deleteAccount() {
	const { error } = await supabase.rpc("delete_my_account");
	if (error) {
		const missing = error.code === "PGRST202" || /could not find the function/i.test(error.message ?? "");
		const err = new Error(missing ? "Account deletion isn't set up on the server yet." : error.message);
		if (missing) err.code = "NOT_SET_UP";
		throw err;
	}
	await wipeLocalCollection();
	clearMigrationFlag();
	try {
		await supabase.auth.signOut();
	} catch {
		// the user no longer exists — the local session is cleared regardless
	}
}

// ---------- password reset ----------

// Sends the reset email. The link returns to the app ROOT with a recovery token
// in the URL hash (`#access_token=…&type=recovery`). We deliberately point at the
// origin, not `#/reset-password`, so it doesn't collide with the hash router or
// the token fragment: supabase-js (detectSessionInUrl) consumes the fragment and
// fires a PASSWORD_RECOVERY auth event, which main.js routes to /reset-password.
// NOTE: this origin must be listed under Supabase → Authentication → URL
// Configuration → Redirect URLs (e.g. http://localhost:5173 for dev).
export async function requestPasswordReset(email) {
	const redirectTo = `${window.location.origin}/`;
	const { error } = await supabase.auth.resetPasswordForEmail(email, {
		redirectTo,
	});
	if (error) throw error;
}

// Called from the reset-password screen while the recovery session is active.
export async function updatePassword(newPassword) {
	const { error } = await supabase.auth.updateUser({ password: newPassword });
	if (error) throw error;
}

// ---------- profile ----------

export async function getProfile(userId) {
	const { data, error } = await supabase
		.from("profiles")
		.select("id, display_name, handle, bio, created_at, updated_at")
		.eq("id", userId)
		.single();
	if (error) throw error;
	return data;
}

export async function updateProfile(userId, fields) {
	const { data, error } = await supabase
		.from("profiles")
		.update(fields)
		.eq("id", userId)
		.select()
		.single();
	if (error) throw error;
	return data;
}

// Handle rules mirror the DB CHECK (`^[a-z0-9_]{3,30}$`, case-insensitive
// unique) so we can validate + check availability before the round-trip.
export const HANDLE_RE = /^[a-z0-9_]{3,30}$/;

// Returns { available, reason }. `reason` is set when not available so the UI can
// distinguish "bad format" from "already taken".
export async function checkHandle(handle, selfId) {
	const value = (handle ?? "").trim().toLowerCase();
	if (!HANDLE_RE.test(value)) {
		return {
			available: false,
			reason: "3–30 chars, lowercase letters, numbers, underscore",
		};
	}
	let query = supabase.from("profiles").select("id").eq("handle", value);
	if (selfId) query = query.neq("id", selfId);
	const { data, error } = await query;
	if (error) throw error;
	if (data.length > 0) return { available: false, reason: "Already taken" };
	return { available: true, reason: null };
}
